import { createContext, ReactNode, useEffect, useState } from 'react'
import { MIN_OUTPUT_VIDEO_DURATION, MAX_OUTPUT_VIDEO_DURATION } from '../../../../../constants'
import CreationToolRating from '../../../../../types/rating'
import SuperCutSegmentationType from '../../../../../types/super-cut-segmentation-type'
import CreationToolWatermarkPosition from '../../../../../types/watermark'
import { ICustomFont, INewCaptureInfo, IWatermark, ICaptionFile } from '../../types'
import CreationToolCrop from '../../../../../../creation-tools/types/crop'
import { SourceEditorCaptionStyle } from '../../../../source-editor/types'
import CreationToolFont from '../../../../../../creation-tools/types/font'
import CreationToolFontSize from '../../../../../../creation-tools/types/font-size'
import { isEqual } from 'lodash'

export type CreationCaptureInfoContextProps = {
    title: string
    tags: string[]
    username: string
    watermark?: IWatermark
    caption?: ICaptionFile
    font?: ICustomFont
    isWatermarkOpen: boolean
    isCustomFontOpen: boolean
    previousWatermarks: IWatermark[]
    previousFonts: ICustomFont[]
    watermarkPosition: CreationToolWatermarkPosition
    sourceUrl: string
    rating: CreationToolRating
    isPublic: boolean
    segmentationType: SuperCutSegmentationType
    segmentationMinLength: number
    segmentationMaxLength: number
    cropType: CreationToolCrop
    autoCrop: boolean
    captionStyle: SourceEditorCaptionStyle
    autoCaption: boolean
    hasAutoCaption: boolean
    languages: string[]
    language: string
    description: string
    addTags: (tags: string[]) => void
    removeTag: (tag: string) => void
    setTitle: (title: string) => void
    setUsername: (username: string) => void
    setWatermark: (watermark?: IWatermark) => void
    setCaption: (caption?: ICaptionFile) => void
    setFont: (font?: ICustomFont) => void
    setIsWatermarkOpen: (isWatermarkOpen: boolean) => void
    setIsCustomFontOpen: (isCustomFontOpen: boolean) => void
    setPreviousWatermarks: (watermarks: IWatermark[]) => void
    setPreviousFonts: (fonts: ICustomFont[]) => void
    setWatermarkPosition: (position: CreationToolWatermarkPosition) => void
    setSourceUrl: (url: string) => void
    setRating: (rating: CreationToolRating) => void
    setIsPublic: (isPublic: boolean) => void
    setSegmentationType: (segmentaionType: SuperCutSegmentationType) => void
    setSegmentationMinLength: (length: number) => void
    setSegmentationMaxLength: (length: number) => void
    setCropType: (cropType: CreationToolCrop) => void
    setAutoCrop: (autoCrop: boolean) => void
    setCaptionStyle: (style: SourceEditorCaptionStyle) => void
    setAutoCaption: (autoCaption: boolean) => void
    setHasAutoCaption: (hasAutoCaption: boolean) => void
    setLanguages: (languages: string[]) => void
    setLanguage: (language: string) => void
    setDescription: (description: string) => void
}

export const CreationCaptureInfoContext = createContext({} as CreationCaptureInfoContextProps)

const cleanUpTags = (tags: string[]) => tags.map((tag) => tag.replace(/#/g, '').trim())

type Props = {
    children?: ReactNode
    defaultTitle?: string
    defaultTags?: string[]
    defaultUsername?: string
    defaultWatermark?: IWatermark
    defaultFont?: ICustomFont
    defaultCaption?: ICaptionFile
    defaultSourceUrl?: string
    defaultPreviousWatermarks?: IWatermark[]
    defaultWatermarkPosition?: CreationToolWatermarkPosition
    defaultPreviousFonts?: ICustomFont[]
    defaultRating?: CreationToolRating
    defaultIsPublic?: boolean
    defaultSegmentationType?: SuperCutSegmentationType
    defaultSegmentationMinLength?: number
    defaultSegmentationMaxLength?: number
    defaultCropType?: CreationToolCrop
    defaultAutoCrop?: boolean
    defaultAutoCaption?: boolean
    defaultHasAutoCaption?: boolean
    defaultLanguages?: string[]
    defaultLanguage?: string
    defaultDescription?: string
    onUpdate?: (hasChanges: boolean, info: INewCaptureInfo) => void
}

const CreationCaptureInfoManager = ({
    children,
    defaultTitle = '',
    defaultTags = [],
    defaultUsername = '',
    defaultWatermark,
    defaultFont,
    defaultCaption,
    defaultSourceUrl = '',
    defaultPreviousWatermarks = [],
    defaultPreviousFonts = [],
    defaultRating = CreationToolRating.Unrated,
    defaultWatermarkPosition = CreationToolWatermarkPosition.BottomRight,
    defaultIsPublic = true,
    defaultSegmentationType = SuperCutSegmentationType.Scene,
    defaultSegmentationMinLength = MIN_OUTPUT_VIDEO_DURATION,
    defaultSegmentationMaxLength = MAX_OUTPUT_VIDEO_DURATION,
    defaultCropType = CreationToolCrop.Original,
    defaultAutoCaption = false,
    defaultAutoCrop = false,
    defaultHasAutoCaption = false,
    defaultLanguages = [],
    defaultLanguage = '',
    defaultDescription = '',
    onUpdate,
}: Props) => {
    const [title, setTitle] = useState<string>(defaultTitle)
    const [tags, setTags] = useState<string[]>(defaultTags)
    const [username, setUsername] = useState<string>(defaultUsername)
    const [watermark, setWatermark] = useState<IWatermark | undefined>(defaultWatermark)
    const [caption, setCaption] = useState<ICaptionFile | undefined>(defaultCaption)
    const [previousWatermarks, setPreviousWatermarks] = useState<IWatermark[]>(defaultPreviousWatermarks)
    const [font, setFont] = useState<ICustomFont | undefined>(defaultFont)
    const [previousFonts, setPreviousFonts] = useState<ICustomFont[]>(defaultPreviousFonts)
    const [isWatermarkOpen, setIsWatermarkOpen] = useState<boolean>(false)
    const [isCustomFontOpen, setIsCustomFontOpen] = useState<boolean>(false)
    const [sourceUrl, setSourceUrl] = useState<string>(defaultSourceUrl)
    const [rating, setRating] = useState<CreationToolRating>(defaultRating)
    const [watermarkPosition, setWatermarkPosition] = useState<CreationToolWatermarkPosition>(defaultWatermarkPosition)
    const [isPublic, setIsPublic] = useState<boolean>(defaultIsPublic)
    const [segmentationType, setSegmentationType] = useState<SuperCutSegmentationType>(defaultSegmentationType)
    const [segmentationMinLength, setSegmentationMinLength] = useState<number>(defaultSegmentationMinLength)
    const [segmentationMaxLength, setSegmentationMaxLength] = useState<number>(defaultSegmentationMaxLength)
    const [cropType, setCropType] = useState<CreationToolCrop>(defaultCropType)
    const [autoCaption, setAutoCaption] = useState<boolean>(defaultAutoCaption)
    const [autoCrop, setAutoCrop] = useState<boolean>(defaultAutoCrop)
    const [hasAutoCaption, setHasAutoCaption] = useState<boolean>(defaultHasAutoCaption)
    const [languages, setLanguages] = useState<string[]>(defaultLanguages)
    const [language, setLanguage] = useState<string>(defaultLanguage)
    const [description, setDescription] = useState<string>(defaultDescription)
    const [captionStyle, setCaptionStyle] = useState<SourceEditorCaptionStyle>({
        color: '#fff',
        font: font?.font ? CreationToolFont.Custom : CreationToolFont.Giphy,
        fontSize: CreationToolFontSize.Medium,
    })

    useEffect(() => {
        if (!onUpdate) return

        const hasChanges =
            watermark !== defaultWatermark ||
            font !== defaultFont ||
            title !== defaultTitle ||
            !isEqual(tags, defaultTags) ||
            username !== defaultUsername ||
            sourceUrl !== defaultSourceUrl ||
            rating !== defaultRating ||
            watermarkPosition !== defaultWatermarkPosition ||
            segmentationType !== defaultSegmentationType ||
            isPublic !== defaultIsPublic ||
            autoCaption !== defaultAutoCaption ||
            autoCrop !== defaultAutoCrop ||
            description !== defaultDescription

        onUpdate(hasChanges, {
            title,
            username,
            tags,
            watermark,
            font,
            rating,
            sourceUrl,
            watermarkPosition,
            captionStyle: segmentationType === SuperCutSegmentationType.Caption ? captionStyle : undefined,
            isPublic,
            autoCaption,
            autoCrop,
            description,
        })
    }, [
        title,
        tags,
        username,
        watermark,
        font,
        rating,
        sourceUrl,
        watermarkPosition,
        segmentationType,
        captionStyle,
        isPublic,
        autoCaption,
        autoCrop,
        description,
        onUpdate,
    ])

    const addTags = (newTags: string[]) => {
        if (newTags.length < 1) return

        const cleaned = cleanUpTags(newTags)
        const allTags = [...tags, ...cleaned]
        setTags(allTags.filter((tag, i) => allTags.indexOf(tag) === i))
    }

    const removeTag = (tag: string) => {
        if (!tag) return

        const cleaned = cleanUpTags([tag])
        setTags((tags) => tags.filter((tag) => cleaned.indexOf(tag) < 0))
    }

    return (
        <CreationCaptureInfoContext.Provider
            value={{
                title,
                tags,
                username,
                watermark,
                font,
                caption,
                isWatermarkOpen,
                isCustomFontOpen,
                previousWatermarks,
                previousFonts,
                watermarkPosition,
                sourceUrl,
                rating,
                isPublic,
                segmentationType,
                segmentationMinLength,
                segmentationMaxLength,
                cropType,
                captionStyle,
                autoCaption,
                autoCrop,
                hasAutoCaption,
                languages,
                language,
                description,
                addTags,
                removeTag,
                setTitle,
                setUsername,
                setWatermark,
                setFont,
                setCaption,
                setIsWatermarkOpen,
                setIsCustomFontOpen,
                setPreviousWatermarks,
                setPreviousFonts,
                setWatermarkPosition,
                setSourceUrl,
                setRating,
                setIsPublic,
                setSegmentationType,
                setSegmentationMinLength,
                setSegmentationMaxLength,
                setCropType,
                setAutoCrop,
                setCaptionStyle,
                setAutoCaption,
                setHasAutoCaption,
                setLanguages,
                setLanguage,
                setDescription,
            }}
        >
            {children}
        </CreationCaptureInfoContext.Provider>
    )
}

export default CreationCaptureInfoManager
